import React, {ViewTransition, startTransition, useCallback, useEffect, useRef, useState} from 'react';
import styled, { keyframes } from 'styled-components';
import {viewTransitionNew, viewTransitionOld} from '../utils/viewTransition';

export type ToastSide = 'left' | 'right';

export type ToastOptions = {
  side?: ToastSide;
  timeout?: number;
};

type ToastRecord = {
  id: string;
  element: React.ReactNode;
  side: ToastSide;
  timeout?: number;
};

type ToastListener = (event: { type: 'add'; toast: ToastRecord } | { type: 'remove'; id: string }) => void;

const listeners = new Set<ToastListener>();

const generateId = () => `toast_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export function openToastNotification(
  element: React.ReactNode | ((close: () => void) => React.ReactNode),
  opts: ToastOptions = {}
) {
  const id = generateId();
  const side = opts.side ?? 'right';
  const close = () => closeToastNotification(id);
  const resolvedElement = typeof element === 'function' ? element(close) : element;
  const toast: ToastRecord = {
    id,
    element: resolvedElement,
    side,
    timeout: opts.timeout,
  };

  listeners.forEach(listener => listener({ type: 'add', toast }));
  return id;
}

export function closeToastNotification(id: string) {
  listeners.forEach(listener => listener({ type: 'remove', id }));
}

const slideLeftEntering = keyframes`
  from {
    transform: translateX(-120%);
  }
  to {
    transform: translateX(0);
  }
`;



const slideLeftExiting = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-120%);
  }
`;

const slideRightEntering = keyframes`
  from {
    transform: translateX(120%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideRightExiting = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(120%);
  }
`;

const Container = styled.div`
  position: fixed;
  bottom: 24px;
  left: 24px;
  display: flex;
  flex-direction: column;
  z-index: 9999;

  @media (min-width: 1240px) {
    bottom: 32px;
    left: 32px;
  }

  @media (min-width: 1440px) {
    bottom: 64px;
    left: 64px;
  }

  &.toast-container-side-right {
    left: auto;
    right: 24px;

    @media (min-width: 1240px) {
      bottom: 32px;
      right: 32px;
    }

    @media (min-width: 1440px) {
      bottom: 64px;
      right: 64px;
    }

  }
`;

const TOAST_ANIMATION_CACHE: Partial<Record<ToastSide, any>> = {};
const toastAnimations = (side: ToastSide) => {
  if (!TOAST_ANIMATION_CACHE[side]) {
    TOAST_ANIMATION_CACHE[side] = [
      viewTransitionNew`
      animation-name: ${side === 'right' ? slideRightEntering : slideLeftEntering};
      animation-duration: 0.4s;
      animation-fill-mode: forwards;
    `(),
      viewTransitionOld`
      animation-name: ${side === 'right' ? slideRightExiting : slideLeftExiting};
      animation-duration: 0.3s;
      animation-fill-mode: forwards;
    `()
    ]
  }
  return TOAST_ANIMATION_CACHE[side];
}

const ToastSlot = styled.div`
  display: inline-flex;
  margin-top: 24px;

  .toast-container-side-right & {
    transform: translate(120%, 0);
  }
`;

const INTERACTION_DISMISS_THRESHOLD_MS = 5000;

type ToastItemProps = { toast: ToastRecord; removeToast: (id: string) => void };

const ToastItem: React.FC<ToastItemProps> = ({ toast, removeToast }) => {
  const shownAt = useRef(Date.now());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const elapsed = Date.now() - shownAt.current;
      if (elapsed >= INTERACTION_DISMISS_THRESHOLD_MS) {
        removeToast(toast.id);
      } else if (ref.current?.contains(e.target as Node)) {
        removeToast(toast.id);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [toast.id, removeToast]);

  return <ToastSlot ref={ref}>{toast.element}</ToastSlot>;
};

export const ToastProvider: React.FC = () => {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const [activeSide, setActiveSide] = useState<ToastSide | null>(null);
  const timeoutsRef = useRef<Record<string, number>>({});
  const activeSideRef = useRef<ToastSide | null>(null);

  const setActiveSideValue = useCallback((side: ToastSide | null) => {
    activeSideRef.current = side;
    setActiveSide(side);
  }, []);

  const removeToast = useCallback((id: string) => {
    startTransition(() => setToasts(prev => {
      const next = prev.filter(toast => toast.id !== id);
      if (next.length === 0) {
        setActiveSideValue(null);
      }
      return next;
    }));
    const timeoutId = timeoutsRef.current[id];
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      delete timeoutsRef.current[id];
    }
  }, [setActiveSideValue]);

  useEffect(() => {
    const listener: ToastListener = (event) => {
      if (event.type === 'add') {
        const resolvedSide = activeSideRef.current ?? event.toast.side;
        if (!activeSideRef.current) {
          setActiveSideValue(resolvedSide);
        }
        startTransition(() => setToasts(prev => [...prev, { ...event.toast, side: resolvedSide }]));
        if (event.toast.timeout && event.toast.timeout > 0) {
          timeoutsRef.current[event.toast.id] = window.setTimeout(() => removeToast(event.toast.id), event.toast.timeout);
        }
      } else {
        removeToast(event.id);
      }
    };

    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      Object.values(timeoutsRef.current).forEach(timeoutId => window.clearTimeout(timeoutId));
      timeoutsRef.current = {};
    };
  }, [removeToast]);

  const [enterAnimation, exitAnimation] = toastAnimations(activeSide ?? 'right');
  return toasts.length > 0 && (
    <ViewTransition enter={enterAnimation} exit={exitAnimation}>
      <Container data-testid='toast-container'  className={`toast-container toast-container-side-${activeSide ?? 'right'}`}>
      {toasts.map(toast => (
        <ViewTransition key={toast.id} enter={enterAnimation} exit={exitAnimation}>
          <ToastItem key={toast.id} toast={toast} removeToast={removeToast} />
        </ViewTransition>
      ))}
    </Container>
    </ViewTransition>
  )
};

export default ToastProvider;
